import React, { useState } from "react";
import GitHubIcon from "@mui/icons-material/GitHub";
import SearchIcon from "@mui/icons-material/Search";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  accentText,
  mutedText,
  filledButton,
  Themed,
  Phone,
  TourBar,
  FeatureTiles,
  TourStep,
  InternalsGrid,
  ApkDownload,
  ApkNote,
  useActiveStep,
} from "../components/Walkthrough";

import appIcon from "../assets/icons/QuickNotes_AppIcon.png";
import makeNoteLight from "../assets/demo_screenshots/quicknotes/make-note-light.webp";
import makeNoteDark from "../assets/demo_screenshots/quicknotes/make-note-dark.webp";
import foldersLight from "../assets/demo_screenshots/quicknotes/folders-light.webp";
import foldersDark from "../assets/demo_screenshots/quicknotes/folders-dark.webp";
import notesLight from "../assets/demo_screenshots/quicknotes/notes-light.webp";
import notesDark from "../assets/demo_screenshots/quicknotes/notes-dark.webp";
import quickTimesLight from "../assets/demo_screenshots/quicknotes/quick-times-light.webp";
import quickTimesDark from "../assets/demo_screenshots/quicknotes/quick-times-dark.webp";
import repeatLight from "../assets/demo_screenshots/quicknotes/repeat-light.webp";
import repeatDark from "../assets/demo_screenshots/quicknotes/repeat-dark.webp";
import weekdaysLight from "../assets/demo_screenshots/quicknotes/weekdays-light.webp";
import weekdaysDark from "../assets/demo_screenshots/quicknotes/weekdays-dark.webp";
import placeArriveLight from "../assets/demo_screenshots/quicknotes/place-arrive-light.webp";
import placeArriveDark from "../assets/demo_screenshots/quicknotes/place-arrive-dark.webp";
import placeLeaveLight from "../assets/demo_screenshots/quicknotes/place-leave-light.webp";
import placeLeaveDark from "../assets/demo_screenshots/quicknotes/place-leave-dark.webp";
import notificationLight from "../assets/demo_screenshots/quicknotes/notification-light.webp";
import notificationDark from "../assets/demo_screenshots/quicknotes/notification-dark.webp";
import upcomingLight from "../assets/demo_screenshots/quicknotes/upcoming-light.webp";
import upcomingDark from "../assets/demo_screenshots/quicknotes/upcoming-dark.webp";
import historyLight from "../assets/demo_screenshots/quicknotes/history-light.webp";
import historyDark from "../assets/demo_screenshots/quicknotes/history-dark.webp";
import searchLaundryLight from "../assets/demo_screenshots/quicknotes/search-laundry-light.webp";
import searchLaundryDark from "../assets/demo_screenshots/quicknotes/search-laundry-dark.webp";
import searchGiftLight from "../assets/demo_screenshots/quicknotes/search-gift-light.webp";
import searchGiftDark from "../assets/demo_screenshots/quicknotes/search-gift-dark.webp";
import searchTargetLight from "../assets/demo_screenshots/quicknotes/search-target-light.webp";
import searchTargetDark from "../assets/demo_screenshots/quicknotes/search-target-dark.webp";
import searchNovemberLight from "../assets/demo_screenshots/quicknotes/search-november-light.webp";
import searchNovemberDark from "../assets/demo_screenshots/quicknotes/search-november-dark.webp";

const REPO = "ElAyachie/Quick-Notes";
const GITHUB_URL = `https://github.com/${REPO}`;
const APK_FILE = "QuickNotes.apk";
const MIN_ANDROID = "8.0";

const shots = {
  makeNote: { light: makeNoteLight, dark: makeNoteDark },
  folders: { light: foldersLight, dark: foldersDark },
  notes: { light: notesLight, dark: notesDark },
  quickTimes: { light: quickTimesLight, dark: quickTimesDark },
  repeat: { light: repeatLight, dark: repeatDark },
  weekdays: { light: weekdaysLight, dark: weekdaysDark },
  placeArrive: { light: placeArriveLight, dark: placeArriveDark },
  placeLeave: { light: placeLeaveLight, dark: placeLeaveDark },
  notification: { light: notificationLight, dark: notificationDark },
  upcoming: { light: upcomingLight, dark: upcomingDark },
  history: { light: historyLight, dark: historyDark },
  searchLaundry: { light: searchLaundryLight, dark: searchLaundryDark },
  searchGift: { light: searchGiftLight, dark: searchGiftDark },
  searchTarget: { light: searchTargetLight, dark: searchTargetDark },
  searchNovember: { light: searchNovemberLight, dark: searchNovemberDark },
};

// The tour follows one note from being written to reminding you, and to being found again. Each
// step is a section of the page with its own anchor, listed in the sticky bar.
const steps = [
  {
    id: "write",
    label: "Write",
    title: "Write it down",
    body: "Quick Notes opens on Make Note. Type a title and whatever you need to remember, pick a folder, and save.",
    screens: [{ shot: shots.makeNote, caption: "Make Note", alt: "A new note titled Farmers market, filed in the Errands folder" }],
  },
  {
    id: "organize",
    label: "Organize",
    title: "Keep it organized",
    body: "Collection lists your folders and how many notes each one holds. Open a folder to see its notes, most recently edited first. A note can move to another folder at any time, and Unclassified is always there for anything that doesn't fit.",
    screens: [
      { shot: shots.folders, caption: "Folders", alt: "The Collection tab listing folders with their note counts" },
      { shot: shots.notes, caption: "Notes in a folder", alt: "The notes in the Errands folder, each with a preview and date" },
    ],
  },
  {
    id: "time",
    label: "Time",
    title: "Pick a time in one tap",
    body: "Every note's menu offers a reminder at a time or at a place. For a time, Later today, Tomorrow morning and Next week each show the exact time they stand for, so one tap sets it. For any other moment, Pick date and time… opens a calendar and a clock.",
    screens: [{ shot: shots.quickTimes, caption: "Quick times", alt: "The Remind me dialog with Later today, Tomorrow morning, Next week and Pick date and time" }],
  },
  {
    id: "repeat",
    label: "Repeat",
    title: "Make it repeat",
    body: "A reminder can come back every day, week, month or year, or on chosen days of the week, like an alarm clock. Repeats count from the first date, so a reminder for the 31st returns to the 31st after a shorter month.",
    screens: [
      { shot: shots.repeat, caption: "How often", alt: "The Repeat dialog: does not repeat, every day, every week, specific days of the week, every month, every year" },
      { shot: shots.weekdays, caption: "Chosen days", alt: "The Repeat on dialog with Tuesday and Saturday ticked" },
    ],
  },
  {
    id: "place",
    label: "Place",
    title: "Get reminded at a place",
    body: "Tap the map to drop a pin and choose how close counts, from 100 m to 2 km. When I arrive suits a library run; When I leave catches you at the door. A place reminder can fire once or on every visit, and it works while the app is closed.",
    detail: "Set an arriving reminder while you're already there, and it waits for the next time you arrive.",
    screens: [
      { shot: shots.placeArrive, caption: "When I arrive", alt: "The place picker with a 200 meter circle around the library in Copley Square, set to When I arrive" },
      { shot: shots.placeLeave, caption: "When I leave", alt: "The place picker with a circle around home in Back Bay, set to When I leave, every time" },
    ],
  },
  {
    id: "notify",
    label: "Notify",
    title: "Deal with it from the notification",
    body: "When a reminder fires, the notification shows the whole note. Done clears it. In 10 min and In 1 hour put it off without opening the app. Tapping the notification opens the note.",
    wide: { shot: shots.notification, alt: "A Quick Notes notification for the Grocery list with Done, In 10 min and In 1 hour buttons", width: 800, height: 580 },
  },
  {
    id: "review",
    label: "Review",
    title: "See what's coming, and what's done",
    body: "Reminders lists everything still waiting under Upcoming, time and place reminders together. Tap one to edit it, open its note or delete it. History keeps every reminder that has fired, including each occurrence of a repeating one.",
    screens: [
      { shot: shots.upcoming, caption: "Upcoming", alt: "Upcoming reminders: one-off, weekdays, monthly, yearly and three place reminders" },
      { shot: shots.history, caption: "History", alt: "History of reminders that have fired, with the time each one fired" },
    ],
  },
  {
    id: "search",
    label: "Search",
    title: "Find it by what you mean",
    body: "Search reaches every folder and understands meaning, not just matching words. “Laundry” finds the note about picking up dry cleaning, and “gift for Mom” turns up a Birthday ideas note that mentions neither. A small language model runs on the phone and turns each note into a vector as it's saved, so nothing is sent anywhere and nothing costs a cent.",
    detail: "Exact words still count: names, ticket numbers and codes are matched by a full-text index alongside.",
    screens: [
      { shot: shots.searchLaundry, caption: "“laundry”", alt: "Searching laundry finds the note Pick up dry cleaning first" },
      { shot: shots.searchGift, caption: "“gift for Mom”", alt: "Searching gift for Mom finds Mom's birthday and a Birthday ideas note" },
    ],
  },
  {
    id: "find-reminders",
    label: "Find",
    title: "Find it by when and where it reminds you",
    body: "Search reads reminders too. “November” lists everything due that month, repeating reminders included. “Target” finds the grocery list whose reminder was set next to one, though the place was only ever called Grocery store. Each result says why it was found.",
    detail: "When a place reminder is set, the shops, schools, landmarks and streets inside its circle are looked up on OpenStreetMap. Only the coordinates leave the phone.",
    screens: [
      { shot: shots.searchTarget, caption: "“target”", alt: "Searching target finds the Grocery list, with the reason: reminder when you arrive at Grocery store, near Target" },
      { shot: shots.searchNovember, caption: "“november”", alt: "Searching november lists Pay rent, Standup notes and Mom's birthday with their November reminder times" },
    ],
  },
];

const features = [
  { icon: SearchIcon, title: "Search by meaning", text: "Finds what you mean, on the phone.", href: "#search" },
  { icon: TravelExploreIcon, title: "Search your reminders", text: "By when they're due, or the stores nearby.", href: "#find-reminders" },
  { icon: AccessTimeIcon, title: "Time reminders", text: "One tap, or repeating on your schedule.", href: "#time" },
  { icon: PlaceOutlinedIcon, title: "Place reminders", text: "When you arrive somewhere, or leave.", href: "#place" },
];

export const tech = ["Java", "Android SDK 36", "Room", "MediaPipe embeddings", "SQLite full-text search", "OpenStreetMap", "Play services geofencing"];

const internals = [
  {
    title: "Search that runs on the phone",
    text: "Notes are split into passages and embedded by Google's Universal Sentence Encoder through MediaPipe, a 6 MB model shipped inside the app. A search compares the query with every stored vector. At the size of one person's notes that takes milliseconds, so there's no vector database to run.",
  },
  {
    title: "Meaning, words and reminders, merged",
    text: "Embeddings miss names and ticket numbers, so SQLite full-text search runs alongside, and reminders are matched by the times and places a query names. Reciprocal rank fusion merges the rankings by position, so a similarity score never has to be weighed against a word count.",
  },
  {
    title: "Measured, then fixed",
    text: "A test runs the real model over 30 notes and 34 labelled queries. As shipped, the model's vectors all pointed the same way and only 57% of queries found a right note in the top three. Subtracting averages taken from separate calibration text, then merging with word search, brought that to 90%.",
  },
  {
    title: "Places it was never told",
    text: "When a place reminder is set, the app asks OpenStreetMap what's inside its circle and keeps the nearest names and kinds, so “Target” or “drugstore” finds a reminder nobody named. Only coordinates leave the phone, and everyday words like “money” don't match the bank on every corner.",
  },
  {
    title: "Reminders that survive",
    text: "Time reminders are exact alarms when allowed; place reminders are Play services geofences, each watching a single crossing. Both are registered again after a reboot or app update. Every firing stays as history, and a repeating reminder continues as a new row.",
  },
  {
    title: "Rebuilt in 2026, still Java",
    text: "First written between 2018 and 2021, when notes were text files. Rebuilt on Room, a single activity and Material 3 at target SDK 36, in the original Java. 126 automated tests cover the recurrence arithmetic, every database migration and search quality.",
  },
];

const stepIds = steps.map((s) => s.id);

function QuickNotesProject() {
  const [appTheme, setAppTheme] = useState(null);
  const active = useActiveStep(stepIds);

  return (
    <div className="w-full text-[black] dark:text-[white] pb-16">
      {/* Hero: text and two phones side by side on large screens, stacked on phones */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-8 lg:pt-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
          <img src={appIcon} alt="" aria-hidden="true" width="192" height="192" className="w-20 h-20 lg:w-24 lg:h-24 drop-shadow-md" />
          <p className={`pt-4 text-sm font-semibold tracking-widest uppercase ${accentText}`}>Android app</p>
          <h1 className="text-[42px] sm:text-5xl lg:text-[56px] leading-tight font-bold pb-3">Quick Notes</h1>
          <p className="text-xl lg:text-[22px] leading-relaxed text-[#333] dark:text-[#d6d9e3]">
            A note-taking app that finds any note by what you mean, with AI that runs on the phone, and
            whose notes remind you of themselves: at a time, on a schedule, or at a place.
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-6">
            <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={filledButton}>
              <GitHubIcon fontSize="small" aria-hidden="true" />
              Source code
            </a>
            <ApkDownload repo={REPO} file={APK_FILE} />
          </div>
          <ApkNote repo={REPO} file={APK_FILE} minAndroid={MIN_ANDROID} className="pt-3" />
          <ul className="flex flex-wrap justify-center lg:justify-start gap-2 pt-6" aria-label="Built with">
            {tech.map((t) => (
              <li key={t} className="rounded-full border border-[#eb4d5c] dark:border-[#c9a7ff] px-3 py-0.5 text-sm font-medium text-[#eb4d5c] dark:text-[#c9a7ff]">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-start justify-center gap-4 lg:gap-6">
          <Phone screen={{ shot: shots.searchNovember, alt: "Quick Notes search for november, listing the notes whose reminders are due that month" }} appTheme={appTheme} size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          <div className="pt-10 lg:pt-14">
            <Phone screen={{ shot: shots.upcoming, alt: "Quick Notes upcoming reminders" }} appTheme={appTheme} size="w-[40vw] max-w-[210px] lg:w-[220px]" />
          </div>
        </div>
      </section>

      {/* What it does, at a glance. Each tile jumps to its part of the tour. */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-14">
        <FeatureTiles features={features} />
      </section>

      {/* The tour */}
      <section className="w-full pt-16" aria-labelledby="tour-heading">
        <div className="w-full max-w-5xl mx-auto px-4">
          <h2 id="tour-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">Take the tour</h2>
          <p className="text-lg lg:text-xl text-center lg:text-left pb-6">
            Follow one note from the moment you write it to the moment it reminds you, and then find it again.
          </p>
        </div>

        <TourBar steps={steps} active={active} appTheme={appTheme} setAppTheme={setAppTheme} />

        <ol className="w-full max-w-5xl mx-auto px-4">
          {steps.map((step, i) => (
            <TourStep key={step.id} step={step} index={i} total={steps.length} appTheme={appTheme}>
              {step.wide && (
                <figure className="w-full max-w-[460px]">
                  <Themed
                    shot={step.wide.shot}
                    appTheme={appTheme}
                    alt={step.wide.alt}
                    width={step.wide.width}
                    height={step.wide.height}
                    className="w-full h-auto rounded-[1.75rem] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)]"
                  />
                  <figcaption className={`pt-3 text-center text-sm font-semibold ${mutedText}`}>The reminder's notification</figcaption>
                </figure>
              )}
            </TourStep>
          ))}
        </ol>
      </section>

      {/* Engineering notes for readers who want to know how it works */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-10" aria-labelledby="built-heading">
        <h2 id="built-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">How it's built</h2>
        <p className="text-lg lg:text-xl text-center lg:text-left pb-7">
          The parts that make search smart and reminders dependable.
        </p>
        <InternalsGrid items={internals} />
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <a href={GITHUB_URL} target="_blank" rel="noreferrer" className={filledButton}>
            <GitHubIcon fontSize="small" aria-hidden="true" />
            Read the code
          </a>
          <ApkDownload repo={REPO} file={APK_FILE} />
        </div>
        <p className={`pt-8 text-center ${mutedText}`}>Created by Mohamed Oulal</p>
      </section>
    </div>
  );
}

export default QuickNotesProject;
